// hooks/useAxiosPrivate.js

import { useEffect, useRef } from "react";
import { axiosPrivateInstance } from "../axios";
import { jwtDecode } from "jwt-decode";
import useAuth from "./useAuth";
import useRefreshToken from "./useRefreshToken";

const TOKEN_REFRESH_BUFFER_MS = 60 * 1000;

function shouldRefreshAccessToken(token) {
  if (!token) return false;

  try {
    const decoded = jwtDecode(token);
    if (!decoded?.exp) return false;

    return decoded.exp * 1000 <= Date.now() + TOKEN_REFRESH_BUFFER_MS;
  } catch {
    return false;
  }
}

export default function useAxiosPrivate() {
  const { accessToken, csrftoken } = useAuth();
  const refresh = useRefreshToken(); // This is now a stable function

  // Use refs to hold the current tokens.
  const accessTokenRef = useRef(accessToken);
  const csrfTokenRef = useRef(csrftoken);

  // This effect keeps the refs in sync with the state from context.
  useEffect(() => {
    accessTokenRef.current = accessToken;
    csrfTokenRef.current = csrftoken;
  }, [accessToken, csrftoken]);


  // ✅ This effect now has stable dependencies and will run only once.
  useEffect(() => {
    const requestIntercept = axiosPrivateInstance.interceptors.request.use(
      async (config) => {
        if (!config.headers) {
          config.headers = {};
        }

        let token = accessTokenRef.current;
        let csrfToken = csrfTokenRef.current;

        if (shouldRefreshAccessToken(token)) {
          const refreshed = await refresh();
          token = refreshed.accessToken;
          csrfToken = refreshed.csrfToken;
          accessTokenRef.current = token;
          csrfTokenRef.current = csrfToken;
        }

        if (token) {
          config.headers["Authorization"] = `Bearer ${token}`;
          if (csrfToken) {
            config.headers["X-CSRFToken"] = csrfToken;
          }
        }

        // When sending FormData, remove Content-Type so the browser sets multipart/form-data with boundary
        if (config.data instanceof FormData && config.headers) {
          delete config.headers["Content-Type"];
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    const responseIntercept = axiosPrivateInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const prevRequest = error?.config;
        if (error?.response?.status === 401 && !prevRequest?.sent) {
          prevRequest.sent = true;
          try {
            const { accessToken: newAccessToken, csrfToken: newCSRFToken } = await refresh();
            accessTokenRef.current = newAccessToken;
            csrfTokenRef.current = newCSRFToken;
            
            if (!prevRequest.headers) {
              prevRequest.headers = {};
            }
            prevRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
            if (newCSRFToken) {
              prevRequest.headers["X-CSRFToken"] = newCSRFToken;
            }
            
            return axiosPrivateInstance(prevRequest);
          } catch (refreshError) {
            console.error("Response Interceptor: Failed to refresh token during retry.", refreshError);
            return Promise.reject(refreshError);
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axiosPrivateInstance.interceptors.request.eject(requestIntercept);
      axiosPrivateInstance.interceptors.response.eject(responseIntercept);
    };
  }, [refresh]);

  return axiosPrivateInstance;
}
